<template>
  <modal
    name="offstreet-property-mapping"
    :classes="'customModal'"
    :scrollable="true"
    :reset="true"
    :adaptive="false"
    :width="1000"
    height="auto"
    @before-open="initializeModal"
  >
    <div class="modalWrapper">
      <div class="content__box">
        <div class="content__box__header d-flex justify-content-between align-items-center">
          <div>
            <p class="text-white txt--ss opacity--50 mb-1">Offstreet Property Mapping</p>
            <p class="txt--xs txt--off-white mb-0">Owner scoped mapping for Offstreet location ids</p>
          </div>
          <button type="button" class="button button--outline button--sm" @click="closeModal">Close</button>
        </div>
        <div class="content__divider"></div>

        <div class="content__box__body">
          <p v-if="!ownerId" class="txt--red txt--xs mb-3">
            Owner context is missing. Reload and sign in again before saving Offstreet property mappings.
          </p>

          <div class="table-responsive">
            <table class="table table__audit table-borderless">
              <thead>
                <tr>
                  <th>Property ID</th>
                  <th>Property Name</th>
                  <th>Offstreet State</th>
                  <th>Offstreet Location ID</th>
                </tr>
              </thead>
              <tbody v-if="properties.length">
                <tr v-for="item in properties" :key="item.property_id">
                  <td>{{ item.property_id }}</td>
                  <td>{{ item.property_name }}</td>
                  <td>
                    <div class="d-flex flex-wrap">
                      <span class="state-pill" :class="item.offstreet_mapping_state === 'mapped' ? 'state-good' : 'state-muted'">
                        {{ item.offstreet_mapping_state === 'mapped' ? 'mapped' : 'unmapped' }}
                      </span>
                      <!-- <span class="state-pill" :class="item.offstreet_configured ? 'state-good' : 'state-muted'">
                        {{ item.offstreet_configured ? 'configured' : 'not configured' }}
                      </span> -->
                      <span class="state-pill" :class="item.offstreet_syncable ? 'state-good' : 'state-warn'">
                        {{ item.offstreet_syncable ? 'syncable' : 'not syncable' }}
                      </span>
                    </div>
                  </td>
                  <td>
                    <input
                      type="text"
                      class="textInputField"
                      placeholder="Offstreet location id"
                      v-model.trim="item.offstreet_location_id"
                    />
                    <p v-if="errorByProperty[item.property_id]" class="txt--xs txt--red mt-1 mb-0">
                      {{ errorByProperty[item.property_id] }}
                    </p>
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="4" class="text-center">
                    <div v-if="loading" class="d-flex justify-content-center">
                      <div class="lds-ring">
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                      </div>
                    </div>
                    <span v-else>No properties found.</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="d-flex justify-content-between align-items-center mt-2">
            <div>
              <p v-if="recreateQueuedCount !== null" class="txt--xs txt--off-white mb-0">
                Recreate queued: {{ recreateQueuedCount }}
              </p>
            </div>
            <ButtonWithSpinner
              class="button button--primary button--sm"
              :loading="saveButton.loading"
              :success="saveButton.success"
              :disabled="saveButton.disabled || !ownerId"
              @click="saveMappings"
            >
              Save Mappings
            </ButtonWithSpinner>
          </div>

          <div v-if="hints.recommended_actions && hints.recommended_actions.length" class="offstreet-hints mt-3">
            <p class="txt--xs txt--off-white mb-1"><strong>Operator hints</strong></p>
            <p class="txt--xs txt--off-white mb-1" v-if="hints.review_subscription_sync_status">
              Review subscription sync status is recommended.
            </p>
            <div class="d-flex flex-wrap align-items-center">
              <span
                class="offstreet-hint-pill"
                v-for="(action, index) in hints.recommended_actions"
                :key="index"
              >
                {{ action }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import ButtonWithSpinner from "@/components/Button";
import { updateOffstreetPropertyConfig } from "@/services/offstreetOperator.service";

export default {
  name: "OffstreetPropertyMapping",
  components: {
    ButtonWithSpinner,
  },
  data() {
    return {
      ownerId: "",
      properties: [],
      loading: false,
      saveButton: {
        loading: false,
        success: false,
        disabled: false,
      },
      errorByProperty: {},
      recreateQueuedCount: null,
      hints: {
        review_subscription_sync_status: false,
        recommended_actions: [],
      },
      selectedPropertyId: "",
    };
  },
  methods: {
    initializeModal(e) {
      this.ownerId = localStorage.getItem("commercial_user_id") || localStorage.getItem("user_id") || "";
      this.errorByProperty = {};
      this.recreateQueuedCount = null;
      this.hints = {
        review_subscription_sync_status: false,
        recommended_actions: [],
      };
      this.selectedPropertyId = e && e.params && e.params.selected_property_id ? String(e.params.selected_property_id) : "";
      this.fetchProperties();
    },
    closeModal() {
      this.$modal.hide("offstreet-property-mapping");
    },
    normalizeHints(rawHints) {
      const hints = rawHints || {};
      return {
        review_subscription_sync_status: hints.review_subscription_sync_status === true,
        recommended_actions: Array.isArray(hints.recommended_actions) ? hints.recommended_actions : [],
      };
    },
    async fetchProperties() {
      this.loading = true;
      this.properties = [];
      try {
        const response = await this.axios.post(window.hostname + "/api/auth/all-space", {
          limit: 200,
          device_type: 1,
          space_type: 1,
          search_param: null,
          status: 4,
          page: 1,
          minify: 1,
        });

        if (response.data && response.data.status && response.data.data && Array.isArray(response.data.data.all_space)) {
          const mapped = response.data.data.all_space.map((property) => {
            const offstreet = property && property.offstreet ? property.offstreet : {};
            const mappingState = String(
              property.offstreet_mapping_state || offstreet.mapping_state || (property.offstreet_mapped || offstreet.mapped ? "mapped" : "unmapped")
            ).toLowerCase();
            const syncableValue = property.offstreet_syncable !== undefined ? property.offstreet_syncable : offstreet.syncable;
            const configuredValue = property.offstreet_configured !== undefined ? property.offstreet_configured : offstreet.configured;
            return {
              property_id: property.space_id,
              property_name: property.property_name || property.address || property.space_id,
              offstreet_location_id: property.offstreet_location_id || offstreet.location_id || "",
              offstreet_mapping_state: mappingState === "mapped" ? "mapped" : "unmapped",
              offstreet_mapped: mappingState === "mapped",
              offstreet_syncable: syncableValue === true || syncableValue === 1 || syncableValue === "1",
              offstreet_configured: configuredValue === true || configuredValue === 1 || configuredValue === "1",
            };
          });

          if (this.selectedPropertyId) {
            mapped.sort((a, b) => {
              if (String(a.property_id) === this.selectedPropertyId) {
                return -1;
              }
              if (String(b.property_id) === this.selectedPropertyId) {
                return 1;
              }
              return 0;
            });
          }

          this.properties = mapped;
        } else {
          this.properties = [];
        }
      } catch (err) {
        this.showError(err);
      } finally {
        this.loading = false;
      }
    },
    showError(err) {
      let message = "Something went wrong. Please try again.";
      if (err && err.response && err.response.data && err.response.data.message) {
        message = err.response.data.message;
      }

      if (err && err.response && err.response.status === 422 && /offstreet|config/i.test(message)) {
        message = "Offstreet credentials are managed by Pied admins. Contact support.";
      }

      this.$toasted.show(message, {
        icon: "times-circle",
        className: "error-class",
      });
    },
    async saveMappings() {
      if (!this.ownerId) {
        this.$toasted.show("Owner context is missing.", {
          icon: "times-circle",
          className: "error-class",
        });
        return;
      }

      this.saveButton.loading = true;
      this.saveButton.disabled = true;
      this.errorByProperty = {};

      try {
        const payload = {
          owner_id: this.ownerId,
          properties: this.properties.map((property) => {
            return {
              property_id: property.property_id,
              offstreet_location_id: property.offstreet_location_id || null,
            };
          }),
        };

        const response = await updateOffstreetPropertyConfig(payload);
        const responseData = response && response.data ? response.data : {};
        const data = responseData.data || responseData || {};

        this.recreateQueuedCount = data.recreate_queued_count !== undefined ? data.recreate_queued_count : null;
        this.hints = this.normalizeHints(data.operator_hints);

        const errors = Array.isArray(data.errors) ? data.errors : [];
        errors.forEach((errorItem) => {
          const propertyId = String(errorItem.property_id || "");
          const message = errorItem.message || errorItem.reason || "Invalid property mapping";
          if (propertyId) {
            this.$set(this.errorByProperty, propertyId, message);
          }
        });

        this.$toasted.show((responseData && responseData.message) || "Offstreet property mappings updated.", {
          icon: "check-circle",
          className: "success-class",
        });
      } catch (err) {
        this.showError(err);
      } finally {
        this.saveButton.loading = false;
        this.saveButton.disabled = false;
      }
    },
  },
};
</script>

<style scoped>
.offstreet-hints {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px;
}

.offstreet-hint-pill {
  font-size: 11px;
  color: #fff;
  background: rgba(24, 154, 255, 0.2);
  border: 1px solid rgba(24, 154, 255, 0.45);
  border-radius: 16px;
  padding: 4px 10px;
  margin-right: 8px;
  margin-bottom: 8px;
}

.state-pill {
  font-size: 11px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 2px 8px;
  margin-right: 6px;
  margin-bottom: 6px;
  text-transform: capitalize;
}

.state-good {
  color: #2cbf7f;
  border-color: rgba(44, 191, 127, 0.45);
}

.state-muted {
  color: #9aa2a9;
  border-color: rgba(154, 162, 169, 0.35);
}

.state-warn {
  color: #f5d97d;
  border-color: rgba(245, 217, 125, 0.45);
}
</style>
